import { API_ENDPOINTS } from '../api/endpoints';

// Hard cap on a single claim. store.makeRequest has early-return paths that
// invoke NO callback — no Cognito token (getToken() null) and a gateway-401
// both set isSessionExpired and return — so onSuccess/onError may never fire
// and the Promise would hang forever (froze the "Taking over…" button, TD-2239).
// This timeout guarantees the claim ALWAYS settles. Fail-open { allowed: true }:
// the cap is usage-management, not security, so a stuck claim must never block
// login nor freeze the take-over modal.
const CLAIM_TIMEOUT_MS = 6000;

// Max time a claim waits for a just-fired /session-end (logout) to commit before
// claiming anyway. Bounds the same-browser logout→login window without ever
// blocking login on a hung release.
const RELEASE_WAIT_MS = 3000;

// The in-flight /session-end release from the last logout in THIS browser, if any.
// Lets the next login-claim wait for the slot to actually free before counting it,
// making the same-browser logout→login sequence deterministic. Cross-machine
// logout→login is handled separately by retry-before-modal in SessionClaimController.
let pendingRelease = null;

/**
 * Record the in-flight /session-end request fired by AuthStore.logout so the
 * next requestSessionClaim can wait for it to commit. Logout stays synchronous;
 * it just hands us the fetch promise.
 *
 * @param {Promise<unknown>} promise the /session-end fetch (or any thenable)
 */
export const recordSessionRelease = (promise) => {
  pendingRelease = promise;
};

// Await (and consume) a pending release, capped at RELEASE_WAIT_MS so a slow or
// hung /session-end never blocks login. Resolves either way.
const awaitPendingRelease = async () => {
  const release = pendingRelease;
  if (!release) return;
  await Promise.race([
    Promise.resolve(release).catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, RELEASE_WAIT_MS))
  ]);
  // Only clear if no newer release was recorded while we waited.
  if (pendingRelease === release) pendingRelease = null;
};

/**
 * POST /session-claim { force } (TD-2239).
 *
 * Resolves with the backend payload:
 *   { allowed, own_slot?, oldest_email?, max_concurrent_users?, no_limit? }
 *
 * Always settles: fail-open { allowed: true } on any error, on a stuck request
 * (CLAIM_TIMEOUT_MS), or on makeRequest's no-callback early returns. A transient
 * hiccup never blocks login nor force-logs-out an active user. The cap is a
 * usage-management control, not a security control.
 *
 * Before claiming it waits (capped) for a pending same-browser logout release so
 * a logout→login in the same tab doesn't count the just-released slot.
 *
 * @param {object} store the root store (exposes makeRequest)
 * @param {boolean} force true to take over the least-recently-active session
 * @returns {Promise<{allowed: boolean, oldest_email?: string|null, max_concurrent_users?: number, own_slot?: boolean, no_limit?: boolean}>}
 */
export const requestSessionClaim = (store, force = false) =>
  new Promise((resolve) => {
    let settled = false;
    let timer = null;
    const done = (value) => {
      if (settled) return;
      settled = true;
      if (timer) clearTimeout(timer);
      resolve(value);
    };

    awaitPendingRelease().then(() => {
      if (settled) return;
      // Catch-all: settle fail-open if the request never calls back / hangs.
      timer = setTimeout(() => done({ allowed: true }), CLAIM_TIMEOUT_MS);
      try {
        store.makeRequest({
          endpoint: API_ENDPOINTS.SESSION_CLAIM,
          body: { force },
          onSuccess: (data) =>
            done(data && typeof data.allowed === 'boolean' ? data : { allowed: true }),
          // Fail-open on any error response.
          onError: () => done({ allowed: true })
        });
      } catch {
        done({ allowed: true });
      }
    });
  });

export default requestSessionClaim;
